/**
 * listActionExecutors — les TROIS verbes de liste du canvas (#994).
 *
 * > `write_markdown` pose une liste NEUVE, avec le reste du document. Ces trois
 * > verbes-là travaillent sur ce que l'avocat a **déjà écrit** : ils
 * > transforment ses paragraphes, ajoutent un item à sa liste, ou font rejoindre
 * > sa numérotation. C'est la moitié manquante — l'agent savait écrire une
 * > liste, il ne savait pas en faire une.
 *
 * ## Ce qui a été MESURÉ avant d'être exposé
 *
 * Relevé le 2026-08-16 sur `superdoc@2.6.0`
 * (`tests/ws-mocked/mesure-994-listes.spec.ts`, projet `mesures`). Aucune de ces
 * formes n'était devinable d'un `.d.ts` — ni le fait que la conversion accepte
 * des paragraphes existants, ni que la numérotation se poursuive au
 * rattachement.
 *
 * 1. **`lists.create({mode:'fromParagraphs'})` accepte des paragraphes qu'il n'a
 *    pas vu naître**, et **le `nodeId` SURVIT à la conversion** : un paragraphe
 *    devient un `listItem` en gardant son identifiant. C'est ce qui rend la
 *    relecture possible sans réapparier par le texte.
 * 2. **`lists.insert` n'insère qu'UN item par appel** (`text`, pas de tableau) :
 *    poser N items est une BOUCLE, et la mesure a confirmé que la numérotation
 *    tient sur cette boucle.
 * 3. **`lists.attach` POURSUIT la numérotation** au lieu de repartir à 1
 *    (marqueurs peints relevés `1.` à `6.`). C'est la raison d'être
 *    d'`attach_numbering` : sans elle, `convert_list` produirait une SECONDE
 *    liste sous la première, indiscernable de la relecture et absurde à l'écran.
 *
 * ## ⚠️ Le `tracked` déclaré ne dit rien du régime obtenu, dans les deux sens
 *
 * `lists.create` s'annonce `tracked:false` et produit pourtant **deux**
 * révisions sous fenêtre `suggesting` ; `lists.insert` s'annonce `tracked:true`
 * et en produit une. Aucun exécuteur de ce module ne déduit donc quoi que ce
 * soit de cette déclaration : il passe `modeDeRevision(ctx)` et laisse
 * `buildVerifiedReceipt` trancher en relisant les identités de révision. C'est
 * aussi pourquoi `convert_list` et `attach_numbering` ont un
 * `reviewedOperations` VIDE au catalogue — l'y déclarer ferait refuser les deux
 * verbes sous le régime suivi.
 *
 * ## ⚠️ Un point NON MESURÉ, et sa conséquence
 *
 * Le nombre de révisions produites par `lists.attach` sous fenêtre `suggesting`
 * n'a pas été relevé (la mesure #994 a porté sur la CONTINUITÉ de numérotation,
 * pas sur le suivi). Or `buildVerifiedReceipt` exige par défaut au moins une
 * identité NEUVE pour prouver l'effet en régime suivi : si le provider n'en
 * produisait aucune, `attach_numbering` rendrait `failed` sur un rattachement
 * que la relecture confirme pourtant. Aucune valeur n'est inventée ici pour s'en
 * prémunir — le jour où la mesure existe, elle tranchera entre « laisser le
 * défaut » et « déclarer `mouvementAttendu:'aucun'` ».
 *
 * ## Le juge est la RELECTURE du `nodeType`, jamais le receipt
 *
 * `doc.insert` HTML rendait déjà `success:true` en posant un paragraphe là où on
 * demandait une liste (#988). La postcondition de ces trois verbes est donc :
 * les nodeIds visés se relisent-ils en `listItem` ?
 *
 * ⚠️ **Doctrine « signal absent ≠ signal négatif »** : un nodeId ABSENT de la
 * relecture n'est PAS une preuve d'échec — la fenêtre de lecture est bornée.
 * Seul un `nodeType` explicitement autre que `listItem` contredit.
 */

import type { CanvasActionInput, CanvasSelector } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { idsDeRevision, type CanvasSnapshot, type CanvasSnapshotBlock } from './canvasSnapshot';
import { detailDuRefus, estUnRefus, resoudreLaCible } from './formatActionExecutors';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { isCompletePublicRange, messageRangeNotInFinalInspection } from './selectorResolver';
import { modeDeRevision, snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/** Les surfaces de liste du Document API, toutes mesurées présentes en 2.6.0. */
interface SurfacesDeListe {
  lists?: {
    create?: MethodeProvider;
    insert?: MethodeProvider;
    attach?: MethodeProvider;
  };
}

type ContexteDeListe = CanvasActionContext & { doc: CanvasActionContext['doc'] & SurfacesDeListe };

export type ConvertListInput = Extract<CanvasActionInput, { action: 'convert_list' }>;
export type AddListItemsInput = Extract<CanvasActionInput, { action: 'add_list_items' }>;
export type AttachNumberingInput = Extract<CanvasActionInput, { action: 'attach_numbering' }>;

/**
 * La fenêtre de RELECTURE de ces verbes — délibérément plus large que celle des
 * verbes de mise en forme.
 *
 * Un verbe de liste vise un intervalle de paragraphes, pas un bloc : la fenêtre
 * de 80 blocs des exécuteurs voisins laisserait hors champ la moitié d'une
 * conversion posée au milieu d'un acte, et la postcondition n'aurait alors
 * aucune matière — verte par ignorance. 500 est la borne déjà retenue par
 * `canvasListCreation` pour la même question.
 */
const FENETRE_DE_RELECTURE = { offset: 0, limit: 500 };

/** Le `nodeType` que le provider donne à un item de liste — la preuve relue. */
const TYPE_DITEM = 'listItem';

const adresseDeBloc = (bloc: CanvasSnapshotBlock): Record<string, unknown> =>
  ({ kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId });

const adresseDItem = (nodeId: string): Record<string, unknown> =>
  ({ kind: 'block', nodeType: TYPE_DITEM, nodeId });

/**
 * Les nodeIds qui se relisent explicitement AUTRE CHOSE qu'un item de liste.
 *
 * ⚠️ Un nodeId absent de `blocs` n'y figure PAS : la fenêtre de relecture est
 * bornée, et une absence dit « je n'ai pas vu », pas « ce n'est pas un item ».
 * Confondre les deux ferait rendre `failed` à une conversion réussie posée
 * au-delà de la fenêtre — c'est-à-dire conseiller à l'agent de la rejouer, donc
 * de doubler la liste de l'avocat.
 */
function nonConvertis(nodeIdsVises: readonly string[], blocs: readonly CanvasSnapshotBlock[]): string[] {
  const typeParNodeId = new Map(blocs.map((bloc) => [bloc.nodeId, bloc.nodeType]));
  return nodeIdsVises.filter((nodeId) => {
    const type = typeParNodeId.get(nodeId);
    return type !== undefined && type !== TYPE_DITEM;
  });
}

/** Combien des nodeIds visés se relisent bien en items — la part PROUVÉE. */
function compteProuve(nodeIdsVises: readonly string[], blocs: readonly CanvasSnapshotBlock[]): number {
  const typeParNodeId = new Map(blocs.map((bloc) => [bloc.nodeId, bloc.nodeType]));
  return nodeIdsVises.filter((nodeId) => typeParNodeId.get(nodeId) === TYPE_DITEM).length;
}

/**
 * Le contrôle de conversion, partagé par `convert_list` et `attach_numbering`.
 *
 * Le message dit les TROIS nombres qui permettent de décider — visés, prouvés,
 * contredits — parce qu'un lecteur hors contexte ne peut pas les reconstituer,
 * et parce que « 0 prouvé, 0 contredit » (tout hors fenêtre) et « 0 prouvé, 4
 * contredits » (rien n'a été converti) n'appellent pas la même conduite.
 */
function controleDeConversion(
  verbe: string,
  nodeIdsVises: readonly string[],
  blocsApres: readonly CanvasSnapshotBlock[],
): VerificationCheck {
  const contredits = nonConvertis(nodeIdsVises, blocsApres);
  const prouves = compteProuve(nodeIdsVises, blocsApres);
  return {
    name: 'list_items_relus',
    passed: contredits.length === 0,
    message: contredits.length === 0
      ? `Relecture : ${prouves}/${nodeIdsVises.length} bloc(s) visé(s) se relisent en item de liste, aucun ne se relit `
        + 'autrement. (Un bloc hors de la fenêtre de relecture n\'est ni prouvé ni infirmé.)'
      : `${verbe} a été annoncé en succès par l'éditeur, mais la relecture rend ${contredits.length} bloc(s) qui ne sont `
        + `PAS des items de liste (nodeIds ${contredits.join(', ')}) sur ${nodeIdsVises.length} visé(s). `
        + 'C\'est le mode d\'échec silencieux du canal HTML : une liste annoncée, des paragraphes posés. '
        + 'Relis le document par canvas_inspect avant toute autre action — ne rejoue pas ce verbe à l\'identique, '
        + 'il poserait une seconde liste.',
  };
}

/**
 * Résout l'intervalle `from`…`to` en blocs consécutifs, ou rend le refus rédigé.
 *
 * `to` absent vaut « un seul bloc ». L'ordre des deux bornes est REMIS À
 * L'ENDROIT si le modèle les a inversées : `lists.create` refuserait un `from`
 * postérieur au `to`, et le contrat du verbe ne dit nulle part que l'ordre est
 * signifiant — mieux vaut convertir ce que l'avocat voit désigné que de refuser
 * sur une convention non écrite.
 */
async function resoudreLIntervalle(
  ctx: ContexteDeListe,
  from: CanvasSelector,
  to: CanvasSelector | undefined,
  verbe: string,
): Promise<{ ok: true; blocs: CanvasSnapshotBlock[]; candidateCount: number } | { ok: false; code: string; message: string }> {
  const debut = await resoudreLaCible(ctx, from, verbe);
  if (!debut.ok) return debut;
  if (to === undefined) return { ok: true, blocs: [debut.target], candidateCount: debut.candidateCount };

  const fin = await resoudreLaCible(ctx, to, verbe);
  if (!fin.ok) return fin;
  if (fin.target.nodeId === debut.target.nodeId) return { ok: true, blocs: [debut.target], candidateCount: debut.candidateCount };

  const premier = Math.min(debut.target.ordinal, fin.target.ordinal);
  const dernier = Math.max(debut.target.ordinal, fin.target.ordinal);
  const fenetre = await snapshot(ctx, { offset: premier - 1, limit: dernier - premier + 1 });
  if (fenetre.blocks.length === 0) {
    return {
      ok: false,
      code: 'SELECTOR_NOT_FOUND',
      message: `${verbe} a été REFUSÉ avant toute écriture : les deux bornes se résolvent (ordinaux ${premier} et `
        + `${dernier}) mais la relecture de l'intervalle ne rend aucun bloc. Rien n'a été modifié. `
        + 'Relance canvas_inspect pour lire le document tel qu\'il est MAINTENANT, puis cible les blocs par nodeId.',
    };
  }
  // `lists.create` / `lists.attach` receive the complete raw interval. Do not
  // let a public `from`/`to` pair silently include a readable-only or malformed
  // middle block that the model projection necessarily omitted.
  if (!isCompletePublicRange(fenetre, premier, dernier, ctx.inspectedPublicNodeIds)) {
    return { ok: false, code: 'TARGET_NOT_IN_FINAL_INSPECTION', message: messageRangeNotInFinalInspection() };
  }
  return { ok: true, blocs: fenetre.blocks, candidateCount: debut.candidateCount };
}

/**
 * Le refus commun « la cible n'est pas un item de liste ».
 *
 * ⚠️ `recovery: 'retry'` et non `reinspect` (#991) : ré-inspecter rendra le même
 * document, avec le même bloc et le même type. Le geste suivant est un AUTRE
 * verbe, et le message le nomme — c'est exactement le champ machine qui
 * contredisait le message en clair et faisait boucler l'agent.
 */
function refusPasUnItem(
  intent: AddListItemsInput['action'] | AttachNumberingInput['action'],
  resume: CanvasSnapshot,
  bloc: CanvasSnapshotBlock,
  role: string,
): CanvasActionReceipt {
  return abortedBeforeMutation({
    intent,
    snapshot: toCanvasSnapshotSummary(resume),
    code: 'NOT_A_LIST_ITEM',
    message: `${intent} a été REFUSÉ avant toute écriture : ${role} (nodeId=${bloc.nodeId}) se relit comme un `
      + `« ${bloc.nodeType} », pas comme un item de liste. Rien n'a été modifié. `
      + 'Ce verbe ne travaille QUE sur une liste qui existe déjà : vise un item de la liste (son texte est '
      + 'cherchable), ou crée d\'abord la liste avec convert_list si ces paragraphes n\'en forment pas encore une.',
    nextAction: 'Vise un bloc dont canvas_inspect rend nodeType "listItem", ou appelle convert_list d\'abord.',
    recovery: {
      action: 'retry',
      reason: 'Rien n\'a été écrit. Ré-inspecter rendrait le même bloc, du même type : la sortie est un autre verbe '
        + 'ou une autre cible, pas une relance à l\'identique.',
    },
  });
}

/**
 * Mettre en LISTE des paragraphes que l'avocat a déjà écrits (#994).
 *
 * ## Pourquoi ce verbe existe, alors que `write_markdown` sait écrire une liste
 *
 * Parce qu'il ne s'agit pas d'écrire : « numérote ces moyens » ne demande aucun
 * texte neuf. Passer par une réécriture ferait perdre les marques de run, les
 * commentaires ancrés et l'historique de révision du passage — et écraserait la
 * frappe de l'avocat pour un résultat qu'une conversion obtient sans y toucher.
 *
 * ## Pourquoi une VRAIE numérotation, et pas un « 1. » écrit à la main
 *
 * `lists.create` pose un `w:numPr` : c'est lui qui fait renuméroter Word quand
 * l'avocat insère une ligne au milieu. Des numéros tapés en dur sont du texte
 * mort, et ils se dérèglent à la première édition — dans un acte communiqué,
 * c'est un moyen mal numéroté.
 *
 * ## ⚠️ `sequence` n'est posé que pour un départ EXPLICITE ≠ 1
 *
 * « Nouvelle séquence à 1 » est déjà le défaut du provider, et le contrat
 * interdit `preset` avec `{mode:'continuePrevious'}` : poser `sequence`
 * systématiquement n'ajouterait rien et fermerait une porte pour rien. Pour
 * poursuivre une numérotation existante, le verbe est `attach_numbering`.
 */
export async function executeConvertList(
  ctx: ContexteDeListe,
  input: ConvertListInput,
): Promise<CanvasActionReceipt> {
  const intervalle = await resoudreLIntervalle(ctx, input.from, input.to, 'convert_list');
  if (!intervalle.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: intervalle.code,
      message: intervalle.message,
    });
  }
  const blocs = intervalle.blocs;
  const pre = await snapshot(ctx, FENETRE_DE_RELECTURE);

  if (typeof ctx.doc.lists?.create !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'convert_list exige editor.doc.lists.create, que cet éditeur n\'expose pas. Rien n\'a été modifié et '
        + 'relancer la même action échouera à l\'identique. Il n\'existe pas de contournement : une liste écrite en '
        + 'texte (« 1. », « - ») ne porte aucune numérotation Word et se dérègle dès que l\'avocat insère une ligne.',
    });
  }

  const premier = blocs[0];
  const dernier = blocs[blocs.length - 1];
  if (premier === undefined || dernier === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: 'convert_list a été REFUSÉ avant toute écriture : l\'intervalle demandé ne contient aucun bloc. '
        + 'Rien n\'a été modifié. Relance canvas_inspect puis cible les paragraphes par nodeId.',
    });
  }

  const nodeIdsVises = blocs.map((bloc) => bloc.nodeId);
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const cible = blocs.length === 1
    ? adresseDeBloc(premier)
    : { from: adresseDeBloc(premier), to: adresseDeBloc(dernier) };

  const resultat = await muterSousFences(
    ctx,
    'convert_list:lists.create',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    () => ctx.doc.lists?.create?.({
      mode: 'fromParagraphs',
      target: cible,
      kind: input.kind,
      preset: input.kind === 'ordered' ? 'decimal' : 'disc',
      ...(input.startAt !== undefined && input.startAt !== 1 ? { sequence: { mode: 'new', startAt: input.startAt } } : {}),
    }, { changeMode: modeDeRevision(ctx), expectedRevision: attendue }),
  );

  const post = await snapshot(ctx, FENETRE_DE_RELECTURE);
  ctx.verifierEpoque('convert_list:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: blocs.map((bloc) => targetSummary(bloc, intervalle.candidateCount)),
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [controleDeConversion('convert_list', nodeIdsVises, post.blocks)],
    effects: [{
      kind: 'convert_list',
      reviewability: 'tracked',
      description: `${nodeIdsVises.length} paragraphe(s) convertis en liste ${input.kind === 'ordered' ? 'numérotée' : 'à puces'}`
        + `${input.startAt !== undefined && input.startAt !== 1 ? ` démarrant à ${input.startAt}` : ''}.`,
      nodeIds: nodeIdsVises,
    }],
  });
}

/**
 * Ajouter des items à une liste qui EXISTE (#994).
 *
 * ## ⚠️ Une boucle, parce que le provider n'insère qu'un item par appel
 *
 * `lists.insert` prend `text`, pas un tableau — mesuré. Poser N items est donc N
 * appels, et la mesure a confirmé que la numérotation tient sur la boucle.
 *
 * ## ⚠️ L'ANCRE avance en mode `after`, et c'est ce qui préserve l'ordre
 *
 * Insérer `[A, B]` « après la cible » en gardant la cible pour ancre donne
 * `cible, B, A` — l'ordre du modèle inversé, sans le moindre refus. L'ancre
 * suit donc l'item qu'on vient de poser. En mode `before`, l'ancre reste la
 * cible : insérer A puis B avant elle donne bien `A, B, cible`.
 *
 * Le `nodeId` de l'item posé vient du receipt (`{success, item:{nodeId}}`,
 * mesuré). S'il manque, la boucle S'ARRÊTE au lieu de continuer sur l'ancienne
 * ancre : livrer les items suivants dans le désordre serait pire que d'en
 * livrer moins et de le dire — un ordre faux ne se voit pas, un item manquant
 * se voit.
 */
export async function executeAddListItems(
  ctx: ContexteDeListe,
  input: AddListItemsInput,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'add_list_items');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const ancreInitiale = resolue.target;
  const pre = await snapshot(ctx, FENETRE_DE_RELECTURE);

  if (ancreInitiale.nodeType !== TYPE_DITEM) {
    return refusPasUnItem(input.action, pre, ancreInitiale, 'la cible désignée');
  }
  if (typeof ctx.doc.lists?.insert !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'add_list_items exige editor.doc.lists.insert, que cet éditeur n\'expose pas. Rien n\'a été modifié et '
        + 'relancer la même action échouera à l\'identique. Écris les items par insert_paragraphs : ils ne porteront '
        + 'pas la numérotation de la liste, dis-le à l\'avocat plutôt que de numéroter à la main.',
    });
  }

  const position = input.position ?? 'after';
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const poses: Array<{ texte: string; nodeId?: string }> = [];
  const echecs: Array<{ texte: string; detail: string }> = [];
  let ancre = ancreInitiale.nodeId;
  let ancragePerdu = false;

  for (const texte of input.texts) {
    const attendue = await revisionCourante(ctx, pre.revision);
    const resultat = await muterSousFences(
      ctx,
      `add_list_items:lists.insert:${poses.length + echecs.length + 1}`,
      { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
      () => ctx.doc.lists?.insert?.({
        target: adresseDItem(ancre),
        position,
        text: texte,
      }, { changeMode: modeDeRevision(ctx), expectedRevision: attendue }),
    );

    if (estUnRefus(resultat)) {
      echecs.push({ texte, detail: detailDuRefus(resultat) });
      break;
    }
    const nodeId = nodeIdDeLItem(resultat);
    poses.push({ texte, ...(nodeId === undefined ? {} : { nodeId }) });
    if (position === 'before') continue;
    if (nodeId === undefined) {
      ancragePerdu = poses.length < input.texts.length;
      break;
    }
    ancre = nodeId;
  }

  const post = await snapshot(ctx, FENETRE_DE_RELECTURE);
  const nodeIdsPoses = poses.flatMap((pose) => pose.nodeId === undefined ? [] : [pose.nodeId]);
  const manquants = poses.filter((pose) => !post.blocks.some((bloc) => bloc.nodeId === pose.nodeId || bloc.fullText.includes(pose.texte)));
  ctx.verifierEpoque('add_list_items:receipt');

  const controles: VerificationCheck[] = [
    {
      name: 'items_inseres',
      passed: echecs.length === 0 && !ancragePerdu && poses.length === input.texts.length,
      message: echecs.length > 0
        ? `L'éditeur a refusé d'insérer « ${apercu(echecs[0]!.texte)} » : ${echecs[0]!.detail}. `
          + `${poses.length} item(s) sur ${input.texts.length} sont DÉJÀ dans la liste — ne rejoue pas le lot entier, `
          + 'il les doublerait ; relis la liste par canvas_inspect et ajoute seulement ce qui manque.'
        : ancragePerdu
          ? `L'éditeur a accepté l'item « ${apercu(poses[poses.length - 1]?.texte ?? '')} » sans rendre son identifiant, `
            + 'donc les items suivants ne pouvaient plus être posés DANS L\'ORDRE demandé. '
            + `${poses.length} item(s) sur ${input.texts.length} ont été insérés ; les autres ne l'ont pas été. `
            + 'Relis la liste par canvas_inspect et ajoute le reste un par un.'
          : `Les ${poses.length} item(s) demandés ont été acceptés par l'éditeur, dans l'ordre.`,
    },
    {
      // La vraie postcondition : le texte demandé se relit dans le document.
      // Elle porte sur le nodeId rendu OU sur le texte, parce qu'un item posé
      // sans identifiant lisible reste un item posé.
      name: 'items_relus',
      passed: manquants.length === 0,
      message: manquants.length === 0
        ? `Relecture : chacun des ${poses.length} item(s) posé(s) est présent dans le document.`
        : `${manquants.length} item(s) annoncés insérés sont INTROUVABLES à la relecture `
          + `(${manquants.map((pose) => `« ${apercu(pose.texte)} »`).join(', ')}). `
          + 'Relis le document par canvas_inspect avant toute autre action.',
    },
  ];

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(ancreInitiale, resolue.candidateCount)],
    provider: { steps: poses.length, refus: echecs },
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: controles,
    effects: [{
      kind: 'add_list_items',
      reviewability: 'tracked',
      description: `${poses.length} item(s) ajoutés ${position === 'after' ? 'après' : 'avant'} l'item visé ; `
        + 'la numérotation de la liste se poursuit toute seule.',
      nodeIds: nodeIdsPoses,
    }],
  });
}

/**
 * Faire REJOINDRE une liste existante à des paragraphes écrits après coup (#994).
 *
 * ## Le fait qui justifie un verbe à part entière
 *
 * `lists.attach` **poursuit** la numérotation de la liste visée — mesuré aux
 * marqueurs peints, qui vont de `1.` à `6.` après rattachement. `convert_list`,
 * lui, crée une nouvelle séquence : appliqué au même besoin, il produirait une
 * SECONDE liste repartant à 1, juste sous la première.
 *
 * ⚠️ Cette différence est **invisible de la relecture** : dans les deux cas les
 * blocs se relisent en `listItem`. Elle ne se voit qu'au marqueur peint, hors
 * d'atteinte d'ici — c'est pourquoi la distinction est portée par la
 * description que le modèle lit AVANT d'appeler, et pas par un contrôle après
 * coup qui ne pourrait pas la faire.
 */
export async function executeAttachNumbering(
  ctx: ContexteDeListe,
  input: AttachNumberingInput,
): Promise<CanvasActionReceipt> {
  const intervalle = await resoudreLIntervalle(ctx, input.from, input.to, 'attach_numbering');
  if (!intervalle.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: intervalle.code,
      message: intervalle.message,
    });
  }
  const ancre = await resoudreLaCible(ctx, input.attachTo, 'attach_numbering');
  if (!ancre.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: ancre.code,
      message: ancre.message,
    });
  }
  const blocs = intervalle.blocs;
  const pre = await snapshot(ctx, FENETRE_DE_RELECTURE);

  if (ancre.target.nodeType !== TYPE_DITEM) {
    return refusPasUnItem(input.action, pre, ancre.target, 'le bloc désigné par « attachTo »');
  }
  if (typeof ctx.doc.lists?.attach !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'attach_numbering exige editor.doc.lists.attach, que cet éditeur n\'expose pas. Rien n\'a été modifié et '
        + 'relancer la même action échouera à l\'identique. convert_list n\'est PAS un remplaçant : il créerait une '
        + 'seconde liste repartant à 1 sous la première.',
    });
  }

  const premier = blocs[0];
  const dernier = blocs[blocs.length - 1];
  if (premier === undefined || dernier === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: 'attach_numbering a été REFUSÉ avant toute écriture : l\'intervalle « from »/« to » ne contient aucun '
        + 'bloc. Rien n\'a été modifié. Relance canvas_inspect puis cible les paragraphes par nodeId.',
    });
  }

  const nodeIdsVises = blocs.map((bloc) => bloc.nodeId);
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const cible = blocs.length === 1
    ? adresseDeBloc(premier)
    : { from: adresseDeBloc(premier), to: adresseDeBloc(dernier) };

  const resultat = await muterSousFences(
    ctx,
    'attach_numbering:lists.attach',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    () => ctx.doc.lists?.attach?.({
      target: cible,
      attachTo: adresseDItem(ancre.target.nodeId),
    }, { changeMode: modeDeRevision(ctx), expectedRevision: attendue }),
  );

  const post = await snapshot(ctx, FENETRE_DE_RELECTURE);
  ctx.verifierEpoque('attach_numbering:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [...blocs, ancre.target].map((bloc) => targetSummary(bloc)),
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [controleDeConversion('attach_numbering', nodeIdsVises, post.blocks)],
    effects: [{
      kind: 'attach_numbering',
      reviewability: 'tracked',
      description: `${nodeIdsVises.length} paragraphe(s) rattachés à la liste de l'item ${ancre.target.nodeId} ; `
        + 'la numérotation se poursuit au lieu de repartir à 1.',
      nodeIds: nodeIdsVises,
    }],
  });
}

/** Le `nodeId` de l'item rendu par `lists.insert` — `{success, item:{nodeId}}`, mesuré. */
function nodeIdDeLItem(resultat: unknown): string | undefined {
  if (typeof resultat !== 'object' || resultat === null) return undefined;
  const item = (resultat as { item?: unknown }).item;
  if (typeof item !== 'object' || item === null) return undefined;
  const nodeId = (item as { nodeId?: unknown }).nodeId;
  return typeof nodeId === 'string' && nodeId.length > 0 ? nodeId : undefined;
}

/** Un texte d'item borné, pour un message d'erreur lisible sans noyer le modèle. */
function apercu(texte: string): string {
  return texte.length > 60 ? `${texte.slice(0, 60)}…` : texte;
}
